import { CommonModule } from '@angular/common';
import { Component, inject, type OnDestroy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, type FormGroup } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { LetDirective } from '@ngrx/component';
import { PorscheDesignSystemModule, ToastManager } from '@porsche-design-system/components-angular';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  skip,
  startWith,
  switchMap,
  tap,
  type Observable,
} from 'rxjs';
import { SubSink } from 'subsink';
import { TableFilterComponent } from '../../shared/components/table-filter/table-filter.component';
import { TableSortComponent } from '../../shared/components/table-sort/table-sort.component';
import type {
  TableSort,
  TableSortDirection,
} from '../../shared/components/table-sort/table-sort.models';
import { objectsEqual } from '../../shared/helpers/objects-equal.helper';
import type { Page } from '../../shared/models/api/page.model';
import type {
  PersonalTravelTransaction,
  PersonalTravelTransactionFilter,
} from '../../shared/models/api/personal-travel-transaction.model';
import type { RemoteUserTripPurposeDTO } from '../../shared/models/api/remote-user-trip-purpose.model';
import type { ControlsOf } from '../../shared/models/controls-of.model';
import { FillArrayPipe } from '../../shared/pipes/fill-array.pipe';
import { OpaCurrencyPipe } from '../../shared/pipes/opa-currency.pipe';
import { PersonalTravelService } from '../../shared/services/api/personal-travel.service';
import { RemoteUserTripPurposeService } from '../../shared/services/api/remote-user-trip-purpose.service';
import { ReportService } from '../../shared/services/api/report.service';
import { MediaQueryService } from '../../shared/services/media-query.service';
import type { PersonalTravelListFormValue } from './personal-travel-list.models';

@Component({
  selector: 'opa-personal-travel-list',
  imports: [
    CommonModule,
    OpaCurrencyPipe,
    PorscheDesignSystemModule,
    RouterLink,
    LetDirective,
    FillArrayPipe,
    TableSortComponent,
    TableFilterComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './personal-travel-list.component.html',
  styleUrl: './personal-travel-list.component.scss',
})
export class PersonalTravelListComponent implements OnDestroy {
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly formBuilder = inject(FormBuilder).nonNullable;
  private readonly remoteUserTripPurposeService = inject(RemoteUserTripPurposeService);
  private readonly personalTravelService = inject(PersonalTravelService);
  private readonly reportService = inject(ReportService);
  private readonly toastManager = inject(ToastManager);

  readonly mediaMaxXs$ = this.mediaQueryService.max('xs');
  private readonly pageNo = new BehaviorSubject(1);
  private readonly subSink = new SubSink();
  readonly allValue = 'all' as const;
  readonly form = this.buildForm();
  private readonly loading = new BehaviorSubject(false);
  readonly loading$ = this.loading.pipe(distinctUntilChanged());
  private readonly exporting = new BehaviorSubject<boolean>(false);
  readonly exporting$ = this.exporting.asObservable();
  private readonly sort = new BehaviorSubject<TableSort | undefined>({
    sortId: 'dateUsed',
    direction: 'desc',
  });
  readonly sort$ = this.sort.pipe(distinctUntilChanged((a, b) => objectsEqual(a, b)));
  readonly tripPurposes$ = this.remoteUserTripPurposeService
    .getRemoteUserTripPurposes()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));
  personalTravelTransactions$ = this.getPersonalTravelTransactions$();

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  reloadPersonalTravelTransactions(): void {
    this.personalTravelTransactions$ = this.getPersonalTravelTransactions$();
  }

  resetFilters(): void {
    this.sort.next(undefined);
    this.form.reset();
  }

  updateSort(sortId: string, direction: TableSortDirection | undefined): void {
    if (!direction) this.sort.next(undefined);
    else this.sort.next({ sortId, direction });
  }

  trackTripPurpose(index: number, purpose: RemoteUserTripPurposeDTO): number {
    return purpose.id;
  }

  trackRow(index: number): number {
    return index;
  }

  updatePageNo(page: number): void {
    this.pageNo.next(page);
  }

  exportPersonalTravelTransactions(): void {
    const form = this.form.getRawValue();
    const name = form.name ?? undefined;
    const tripPurposeId =
      (form.tripPurposeId === this.allValue ? undefined : form.tripPurposeId) ?? undefined;
    const dateFrom = form.dateFrom ?? undefined;
    const dateTo = form.dateTo ?? undefined;

    this.exporting.next(true);
    this.subSink.sink = this.reportService
      .getPersonalTravelTransactions(name, tripPurposeId, dateFrom, dateTo)
      .pipe(
        tap({
          next: () => {
            this.toastManager.addMessage({
              text: 'Personal Travel Transactions exported',
              state: 'success',
            });
            this.exporting.next(false);
          },
          error: () => this.exporting.next(false),
        }),
      )
      .subscribe();
  }

  private buildForm(): FormGroup<ControlsOf<PersonalTravelListFormValue>> {
    const form = this.formBuilder.group<ControlsOf<PersonalTravelListFormValue>>({
      name: this.formBuilder.control(null),
      tripPurposeId: this.formBuilder.control(this.allValue),
      dateFrom: this.formBuilder.control(null),
      dateTo: this.formBuilder.control(null),
    });
    return form;
  }

  private getPersonalTravelTransactions$(): Observable<Page<PersonalTravelTransaction>> {
    const pageNo$ = this.pageNo.pipe(distinctUntilChanged());

    const form$ = merge(
      ...Object.entries(this.form.controls).map(([key, control]) => {
        const controlName = key as keyof PersonalTravelListFormValue;
        let value$ = control.valueChanges as Observable<unknown>;
        value$ = value$.pipe(
          startWith(control.value),
          distinctUntilChanged(),
          skip(1),
          map(() => undefined),
        );
        if (controlName === 'name') value$ = value$.pipe(debounceTime(500));
        return value$;
      }),
    ).pipe(
      startWith(undefined),
      map(() => this.form.getRawValue()),
      tap(() => this.updatePageNo(1)),
    );

    const sort$ = this.sort$.pipe(tap(() => this.updatePageNo(1)));

    return combineLatest([pageNo$, form$, sort$]).pipe(
      debounceTime(0),
      tap(() => this.loading.next(true)),
      switchMap(([pageNo, form, sort]) => {
        const filter: PersonalTravelTransactionFilter = {
          name: form.name ?? undefined,
          tripPurposeId:
            (form.tripPurposeId === this.allValue ? undefined : form.tripPurposeId) ?? undefined,
          dateFrom: form.dateFrom ?? undefined,
          dateTo: form.dateTo ?? undefined,
        };

        return this.personalTravelService.getPersonalTravelTransactions(pageNo, filter, sort);
      }),
      tap({
        next: () => this.loading.next(false),
        error: () => this.loading.next(false),
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }
}
